import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

//primitives/compiled doesn't export xcss

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled â€“ go/akcss
import { Box, Stack, Text, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';

import type { FilterType } from '../QuestionsFilters';
import { QuestionsFilters } from '../QuestionsFilters';
import { QuestionItem } from '../QuestionItem';
import { Layout } from '../Layout';
import { Pagination } from '../Pagination';
import type { Topic } from '../QuestionTopics';

export const QUESTIONS_PER_PAGE = 15;

const i18n = defineMessages({
	noQuestionsFound: {
		id: 'questions.questions-list.no-questions-found',
		defaultMessage: 'No questions found',
		description: 'Text for the message that appears when no questions are found',
	},
});

const ContainerStyles = xcss({
	textAlign: 'center',
	minHeight: '200px',
});

//TODO handle null values when it's implemented
//https://product-fabric.atlassian.net/browse/CCECO-4231
export type Question = {
	content: {
		id: string;
		title: string;
		labels: Array<Topic>;
		space: {
			name?: string;
			alias?: string;
			key: string;
		};
		history: {
			createdDate: string;
			createdBy: {
				displayName: string;
			};
		};
		answersTotalCount: number;
		numberOfVotes: number;
		hasAcceptedAnswer?: boolean;
	};
};

type BaseQuestionsListProps = {
	questions: Question[];
	loading: boolean;
	error?: any;
	asideComponent: React.ReactNode;
	mainContentHeader?: React.ReactNode;
	routerName: string;
	showSpaceName?: boolean;
	pageInfo: {
		hasNextPage: boolean;
		endCursor: string | null;
	};
	fetchMore: any;
	queryVariables: any;
	connectionPath: string;
	onFilterChange: (cql: string, filterType: FilterType) => void;
	labelFilter?: string;
	disableFilter?: boolean;
	spaceKey?: string;
	currentFilter: FilterType;
};

export const BaseQuestionsList: React.FC<BaseQuestionsListProps> = ({
	questions,
	loading,
	asideComponent,
	mainContentHeader,
	routerName,
	showSpaceName,
	pageInfo,
	fetchMore,
	queryVariables,
	connectionPath,
	onFilterChange,
	labelFilter,
	disableFilter,
	spaceKey,
	currentFilter,
}) => {
	const { formatMessage } = useIntl();

	const renderMainContent = () => {
		if (loading) {
			//TODO make spinner and placeholder reusable
			return (
				<Box xcss={ContainerStyles}>
					<Spinner size="medium" label="Loading" />
				</Box>
			);
		}

		if (!questions?.length) {
			return (
				<Box xcss={ContainerStyles}>
					<Text>{formatMessage(i18n.noQuestionsFound)}</Text>
				</Box>
			);
		}

		return (
			<>
				<Stack space="space.250" role="list">
					{questions.map((question) => (
						<QuestionItem
							key={question.content.id}
							createdBy={question.content.history.createdBy.displayName}
							numberOfAnswers={question.content.answersTotalCount}
							//TODO show votes count
							//https://product-fabric.atlassian.net/browse/CCECO-4083
							numberOfVotes={question.content.numberOfVotes}
							createdDate={new Date(question.content.history.createdDate)}
							questionTitle={question.content.title}
							spaceKey={question.content.space.key}
							spaceName={showSpaceName ? question.content.space?.name : ''}
							routerName={routerName}
							questionId={question.content.id}
							topics={question.content.labels}
							hasAcceptedAnswer={question.content.hasAcceptedAnswer}
						/>
					))}
				</Stack>
				<Pagination
					pageInfo={pageInfo}
					fetchMore={fetchMore}
					queryVariables={queryVariables}
					connectionPath={connectionPath}
				/>
			</>
		);
	};

	return (
		<Layout
			rightColumnWidth="large"
			mainContentHeader={mainContentHeader}
			mainContent={
				<Stack space="space.250" alignBlock="start" spread="space-between" grow="fill">
					<QuestionsFilters
						onFilterChange={onFilterChange}
						labelFilter={labelFilter}
						disableFilter={disableFilter}
						spaceKey={spaceKey}
						currentFilter={currentFilter}
					/>
					{renderMainContent()}
				</Stack>
			}
			asideContent={asideComponent}
		/>
	);
};
